// Copyright (c) 2023, Epiphyte LLC
// All rights reserved.
// Author: Fernandez-Alcon, Jose <jose@epiphyte.io>


import {Amplify, Auth} from "aws-amplify";
import React, {createContext, useContext, useEffect, useState} from "react";
import {AwsConfigAuth} from "../SDK/Cognito";
import { Oikos } from "../SDK/Oikos";
import {notification} from "antd";



interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  username: string;
  user: any;
  oikosAdmin: OIKOS.admin;
  signIn: (username: string, password: string) => Promise<Result>;
  signUp: (email: string, name: string, familyName: string, userType: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<Result>;
  resetPassword: (username: string) => Promise<Result>;
  confirmResetPassword: (username: string, code: string, newPassword: string) => Promise<Result>;
}

interface Result {
  success: boolean;
  message: string;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

Amplify.configure({Auth: AwsConfigAuth});

export const ProvideAuth: React.FC<Props> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};



export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState(localStorage.getItem('username') || "");
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || {} as OIKOS.cognitoUser);;
  const [oikosAdmin, setOikosAdmin] = useState<OIKOS.admin>({} as OIKOS.admin);

  const oikos = new Oikos();

  console.log("useProvideAuth")

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((result: any) => {
        console.log(result)
        setUser(result);
        setUsername(result.username);
        const payload = result.signInUserSession.idToken.payload;
        console.log(result.username)
        console.log(payload.email)

        // Store user in local storage
        localStorage.setItem('user', JSON.stringify(result));

        setIsAuthenticated(true);
        setIsLoading(false);
      })
      .catch(() => {
        setUsername("");
        setIsAuthenticated(false);
        setIsLoading(false);
      });
  }, []);

  const signIn = async (username: string, password: string) => {
    console.log("Signing in...");

    try {
      const result = await Auth.signIn(username, password);
      setUsername(result.username);

      const admin: OIKOS.admin = {
        ID: result.username,
        Username: result.username,
        Name: result.attributes.given_name,
        FamilyName: result.attributes.family_name,
        Email: result.attributes.email,
      }
      setOikosAdmin(admin)
      setIsAuthenticated(true);
      notification.success({
        message: "Success",
        description: "Login successful",
      })
      return {success: true, message: ""};
      // return oikos.getUser(result.username).then((oikosUser:OIKOS.user ) => {
      //   console.log(oikosUser)
      //   oikosUser.DisplayName = oikosUser.Name + " " + oikosUser.FamilyName
      //
      //   return oikos.getProfileImage(oikosUser.ID).then((image: string) => {
      //     oikosUser.Picture = image;
      //     setOikosUser(oikosUser)
      //     setIsAuthenticated(true);
      //     notification.success({
      //       message: "Success",
      //       description: "Login successful",
      //     })
      //     return {success: true, message: ""};
      //   })
      // })

    } catch (error) {
      notification.error({
        message: "Error: " + error,
        description: "Invalid username or password",
      });
      return {
        success: false,
        message: "LOGIN FAIL",
      };
    }
  };


  const signUp = async (email: string, name: string, familyName: string, userType: string): Promise<boolean> => {
    return oikos.signUp(email, name, familyName, userType)
      .then((res: any) => {
        if (res.Error) {
          notification.error({
            message: "Sign Up Error",
            description: res.Payload
          });
          return false;
        }
        notification.success({
          message: "Sign Up Successfully",
          description: "Please, confirm your email address"
        });
        return true;

      })
      .catch(err => {
        notification.error({
          message: "Error during sing up",
          description: err
        });
        return false;
      });
  }

  const signOut = async () => {
    try {
      const result = await Auth.signOut();
      setUsername("");
      setOikosAdmin({} as OIKOS.admin);
      setIsAuthenticated(false);
      notification.success({
        message: "Success",
        description: "Logout successful",
      })
      // return { success: true, message: "" };
      return result
    } catch (error) {
      notification.error({
        message: "Error: " + error,
        description: "Logout failed",
      })
      return {
        success: false,
        message: "LOGIN FAIL",
      };
    }
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      const result = await Auth.currentAuthenticatedUser().then(user => {
        return Auth.changePassword(user, oldPassword, newPassword);
      });
      return { success: true, message: "" };
    } catch (error) {
      return {
        success: false,
        message: "Error Changing Password: " + error,
      };
    }
  }

  const resetPassword = async (username: string) => {
    try {
      await Auth.forgotPassword(username);
      // notification.success({
      //   message: "Success",
      //   description: "Password reset link sent to your email.",
      // });
      return { success: true, message: "" };
    } catch (error) {
      // notification.error({
      //   message: "Error: " + error,
      //   description: "Failed to send password reset link.",
      // });
      return { success: false, message: "Error: " + error };
    }
  };

  const confirmResetPassword = async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    try {
      await Auth.forgotPasswordSubmit(username, code, newPassword);
      // notification.success({
      //   message: "Success",
      //   description: "Password reset successfully.",
      // });
      return { success: true, message: "" };
    } catch (error) {
      // notification.error({
      //   message: "Error: " + error,
      //   description: "Failed to reset password.",
      // });
      return { success: false, message: "Error: " + error };
    }
  };


  return {
    isLoading,
    isAuthenticated,
    username,
    user,
    oikosAdmin,
    signIn,
    signUp,
    signOut,
    changePassword,
    resetPassword,
    confirmResetPassword,
  };
};
